import React from 'react';

import {
  type PatientImportIssue,
  type PatientImportIssueCategory,
  type PatientImportJobRow,
  type PatientImportRowResolution,
} from '@eluve/api-contract';
import {
  Button,
  Caption,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Heading,
  Icon,
  type IconName,
  Separator,
  Stack,
  Tag,
  Text,
  cn,
  tv,
} from '@eluve/components';
import { pluralize } from '@eluve/utils';

import { type ImportIssue, type ImportRow } from '../components/RowCard';
import { WhiteCard } from '../components/WhiteCard';

export type Counts = {
  total: number;
  ready: number;
  blocking: number;
  informational: number;
};

export type RowWithIssues = ImportRow & { issues: ImportIssue[] };

export type ImportOutcomeCounts = {
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
};

export const toRowWithIssues = (
  raw: PatientImportJobRow,
  issues: PatientImportIssue[] = [],
): RowWithIssues => ({
  id: raw.id,
  rowNumber: raw.rowNumber,
  patientId: raw.patientId,
  firstName: raw.firstName,
  lastName: raw.lastName,
  dateOfBirth: raw.dateOfBirth,
  email: raw.email,
  phoneNumber: raw.phoneNumber,
  dedupeStatus: raw.dedupeStatus,
  resolution: raw.resolution,
  issues: issues.map((i) => ({
    id: i.id,
    category: i.category,
    blocking: i.blocking,
    message: i.message,
    field: i.field,
    metadata: i.metadata,
  })),
});

type ProjectionBucket =
  | 'willCreate'
  | 'willUpdate'
  | 'willKeepUnchanged'
  | 'willSkip'
  | null;

export type Severity = 'error' | 'warn' | 'info';

type CategoryConfig = {
  label: string;
  severity: Severity;
  blocking: boolean;
  help: string;
};

export const CATEGORY_CONFIG: Record<
  PatientImportIssueCategory,
  CategoryConfig
> = {
  FORMAT_ERROR: {
    label: 'Formatting errors',
    severity: 'error',
    blocking: true,
    help: "A field doesn't match the expected format.",
  },
  MISSING_DATA: {
    label: 'Missing required data',
    severity: 'error',
    blocking: true,
    help: 'One or more required fields are empty.',
  },
  DUPE_IN_FILE: {
    label: 'Duplicate rows in file',
    severity: 'error',
    blocking: true,
    help: 'The same row appears more than once in your file.',
  },
  DUPE_ID_DIFFERENT_PATIENT: {
    label: 'An existing patient in Eluve already has the same patient_id',
    severity: 'error',
    blocking: true,
    help: 'patient_id must be unique.',
  },
  DUPE_ID_SAME_PATIENT: {
    label: 'A similar patient with the same ID already exists in Eluve',
    severity: 'error',
    blocking: true,
    help: 'A patient with the same ID and a similar name exists in Eluve. Their other information may have been updated.',
  },
  PATIENT_EXISTS_NO_ID_MATCH: {
    label: 'A similar patient with a different ID exists in Eluve',
    severity: 'warn',
    blocking: false,
    help: 'Choose whether to keep the existing record, update it with the new one, or keep both as separate records.',
  },
  DUPE_ID_EXACT_MATCH: {
    label: 'Patient already exists in Eluve',
    severity: 'info',
    blocking: false,
    help: 'This patient is already in Eluve with exactly the same info. Nothing will change on import.',
  },
  STALE_CONFLICT: {
    label: 'Stale conflicts',
    severity: 'error',
    blocking: true,
    help: 'The existing record changed since you uploaded. Review before continuing.',
  },
};

const RESOLUTION_BUCKET: Record<PatientImportRowResolution, ProjectionBucket> =
  {
    FIXED: 'willCreate',
    KEEP_BOTH: 'willCreate',
    OVERWRITE: 'willUpdate',
    SKIPPED: 'willSkip',
    REMOVED: 'willSkip',
    KEPT_OLD: 'willSkip',
    UPDATE_UPLOAD: 'willSkip',
  };

export const projectRow = (row: RowWithIssues): ProjectionBucket => {
  if (row.resolution) return RESOLUTION_BUCKET[row.resolution];
  if (row.dedupeStatus === 'NEW') return 'willCreate';
  if (row.dedupeStatus === 'DUPE_ID_EXACT_MATCH') return 'willKeepUnchanged';
  // PATIENT_EXISTS_NO_ID_MATCH is non-blocking; backend commits unresolved
  // rows as KEEP_BOTH (creates new patient). Surface in willCreate so the
  // finalize counter matches what will actually commit.
  if (row.dedupeStatus === 'PATIENT_EXISTS_NO_ID_MATCH') return 'willCreate';
  return null;
};

export const countImportOutcomes = (
  rows: readonly Pick<
    PatientImportJobRow,
    'commitStatus' | 'dedupeStatus' | 'resolution'
  >[],
): ImportOutcomeCounts => {
  const counts: ImportOutcomeCounts = {
    created: 0,
    updated: 0,
    unchanged: 0,
    skipped: 0,
  };

  for (const row of rows) {
    if (row.commitStatus === 'COMMITTED_CREATE') counts.created += 1;
    else if (row.commitStatus === 'COMMITTED_UPDATE') counts.updated += 1;
    else if (row.commitStatus === 'SKIPPED') {
      if (row.dedupeStatus === 'DUPE_ID_EXACT_MATCH' && row.resolution === null)
        counts.unchanged += 1;
      else counts.skipped += 1;
    }
  }

  return counts;
};

export const SummaryTile: React.FC<{ label: string; value: number }> = ({
  label,
  value,
}) => (
  <Card>
    <CardContent>
      <Stack gap={1}>
        <Text color="tertiary" size="xs">
          {label}
        </Text>
        <Heading size="2xl" weight="semibold">
          {value}
        </Heading>
      </Stack>
    </CardContent>
  </Card>
);

type StatTone = 'neutral' | 'ok' | 'warn' | 'err';

const summaryStatStyles = tv({
  slots: {
    icon: 'shrink-0',
    value: '',
  },
  variants: {
    tone: {
      neutral: { icon: 'text-text-tertiary', value: 'text-text-primary' },
      ok: {
        icon: 'text-text-positive-subtle',
        value: 'text-text-positive-subtle',
      },
      warn: { icon: 'text-text-warning', value: 'text-text-warning' },
      err: { icon: 'text-text-negative', value: 'text-text-negative' },
    },
  },
});

const SummaryStat: React.FC<{
  label: string;
  value: number;
  tone?: StatTone;
  icon?: IconName;
}> = ({ label, value, tone = 'neutral', icon }) => {
  const styles = summaryStatStyles({ tone });
  return (
    <Stack
      direction="horizontal"
      gap={3}
      align="center"
      className="min-w-0 flex-1 px-4 py-1"
    >
      {icon ? <Icon name={icon} size="sm" className={styles.icon()} /> : null}
      <Stack gap={0} className="min-w-0">
        <Caption color="tertiary" size="2xs">
          {label}
        </Caption>
        <Heading size="2xl" weight="semibold" className={styles.value()}>
          {value}
        </Heading>
      </Stack>
    </Stack>
  );
};

export const SummaryStrip: React.FC<{ counts: Counts }> = ({ counts }) => {
  const errsClear = counts.blocking === 0;
  const warnsClear = counts.informational === 0;
  return (
    <WhiteCard className="p-2">
      <Stack direction="horizontal" align="stretch" gap={0}>
        <SummaryStat label="Total rows" value={counts.total} />
        <Separator orientation="vertical" className="my-1" />
        <SummaryStat
          label="Errors · must resolve"
          value={counts.blocking}
          tone={errsClear ? 'ok' : 'err'}
          icon={errsClear ? 'Check' : 'TriangleAlert'}
        />
        <Separator orientation="vertical" className="my-1" />
        <SummaryStat
          label="Warnings · needs review"
          value={counts.informational}
          tone={warnsClear ? 'neutral' : 'warn'}
          icon={warnsClear ? undefined : 'CircleAlert'}
        />
        <Separator orientation="vertical" className="my-1" />
        <SummaryStat label="Ready to import" value={counts.ready} tone="ok" />
      </Stack>
    </WhiteCard>
  );
};

type SectionStyleKey = Severity | 'ok';

type SectionStyle = {
  icon: IconName;
  textClass: string;
  bgClass: string;
  borderClass: string;
  tagColor: 'positive' | 'negative' | 'warning' | 'gray';
};

const SECTION_STYLES: Record<SectionStyleKey, SectionStyle> = {
  error: {
    icon: 'TriangleAlert',
    textClass: 'text-text-negative',
    bgClass: 'bg-bg-negative-subtle',
    borderClass: 'border-border-negative-subtle',
    tagColor: 'negative',
  },
  warn: {
    icon: 'CircleAlert',
    textClass: 'text-text-warning',
    bgClass: 'bg-bg-warning-subtle',
    borderClass: 'border-border-warning-subtle',
    tagColor: 'warning',
  },
  info: {
    icon: 'Info',
    textClass: 'text-text-info',
    bgClass: 'bg-bg-info-subtle',
    borderClass: 'border-border-info-subtle',
    tagColor: 'gray',
  },
  ok: {
    icon: 'Check',
    textClass: 'text-text-positive-subtle',
    bgClass: 'bg-bg-positive-subtle',
    borderClass: 'border-border-positive-subtle',
    tagColor: 'positive',
  },
};

const SEVERITY_LABEL: Record<Severity, string> = {
  error: 'Errors · must resolve before import',
  warn: 'Warnings · review before import',
  info: 'Informational · no action needed',
};

export const categoryAllowsSkipActions = (category: string): boolean =>
  category !== 'DUPE_ID_EXACT_MATCH';

export const SECTION_ORDER: Severity[] = ['error', 'warn', 'info'];

export const SectionLabel: React.FC<{
  severity: Severity;
  count: number;
}> = ({ severity, count }) => (
  <Stack direction="horizontal" gap={3} align="center" className="pt-2">
    <Caption size="2xs" className={SECTION_STYLES[severity].textClass}>
      {SEVERITY_LABEL[severity]}
    </Caption>
    <Text inline color="tertiary" size="xs">
      · {pluralize(count, 'row')}
    </Text>
    <Separator className="flex-1" />
  </Stack>
);

type BucketEntry = {
  category: string;
  rows: RowWithIssues[];
  unresolved: number;
  dupeGroups: RowWithIssues[][];
  singletons: RowWithIssues[];
};

export const bucketCategoriesBySeverity = (
  groups: Map<string, RowWithIssues[]>,
): Record<Severity, BucketEntry[]> => {
  const buckets: Record<Severity, BucketEntry[]> = {
    error: [],
    warn: [],
    info: [],
  };
  for (const [category, rows] of groups) {
    if (category === 'NO_ISSUES' || rows.length === 0) continue;
    const severity =
      CATEGORY_CONFIG[category as PatientImportIssueCategory]?.severity ??
      'error';
    const unresolved = rows.reduce(
      (n, r) => (r.resolution === null ? n + 1 : n),
      0,
    );
    // A clique of one (no surviving partner in the bucket) renders as a plain
    // RowCard, not a group card.
    const dupeGroups: RowWithIssues[][] = [];
    const dupeSingletons: RowWithIssues[] = [];
    if (category === 'DUPE_IN_FILE') {
      for (const clique of groupDupeInFileCliques(rows)) {
        if (clique.length > 1) dupeGroups.push(clique);
        else if (clique[0]) dupeSingletons.push(clique[0]);
      }
    }
    const singletons = category === 'DUPE_IN_FILE' ? dupeSingletons : rows;
    buckets[severity].push({
      category,
      rows,
      unresolved,
      dupeGroups,
      singletons,
    });
  }
  return buckets;
};

export const GroupSectionCard: React.FC<{
  category: string;
  rowCount: number;
  unresolved: number;
  onSkipAll?: () => void;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({
  category,
  rowCount,
  unresolved,
  onSkipAll,
  defaultOpen = true,
  children,
}) => {
  const config = CATEGORY_CONFIG[category as PatientImportIssueCategory];
  const severity: Severity = config?.severity ?? 'error';
  const label = config?.label ?? category;
  const help = config?.help;
  const blocking = config?.blocking ?? false;

  const allClear = unresolved === 0 && rowCount > 0;
  const styleKey: SectionStyleKey = allClear ? 'ok' : severity;
  const style = SECTION_STYLES[styleKey];
  const allowSkipActions = categoryAllowsSkipActions(category);

  return (
    <Collapsible defaultOpen={defaultOpen}>
      <Card className={cn('overflow-hidden bg-bg-primary', style.borderClass)}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              'w-full cursor-pointer border-b px-4 py-3 text-left transition-colors',
              style.bgClass,
              style.borderClass,
            )}
          >
            <Stack
              direction="horizontal"
              justify="between"
              align="start"
              gap={3}
            >
              <Stack
                direction="horizontal"
                gap={3}
                align="start"
                className="min-w-0 flex-1"
              >
                <Icon
                  name={style.icon}
                  size="md"
                  className={cn('mt-0.5 shrink-0', style.textClass)}
                />
                <Stack gap={1} className="min-w-0">
                  <Stack
                    direction="horizontal"
                    gap={2}
                    align="center"
                    className="flex-wrap"
                  >
                    <Heading
                      size="lg"
                      weight="semibold"
                      className={style.textClass}
                    >
                      {label}
                    </Heading>
                    <Tag
                      size="xs"
                      color={style.tagColor}
                      label={
                        allClear
                          ? `All ${rowCount} resolved`
                          : `${unresolved} of ${pluralize(rowCount, 'row')}`
                      }
                    />
                    {!allClear && blocking && severity !== 'error' ? (
                      <Tag size="xs" color="negative" label="Must resolve" />
                    ) : null}
                  </Stack>
                  {help ? (
                    <Text size="xs" className={style.textClass}>
                      {help}
                    </Text>
                  ) : null}
                </Stack>
              </Stack>
              {allowSkipActions && !allClear && onSkipAll && unresolved > 0 ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSkipAll();
                  }}
                >
                  {category === 'DUPE_IN_FILE'
                    ? 'Skip all duplicate rows'
                    : 'Skip all'}
                </Button>
              ) : null}
            </Stack>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent animated>{children}</CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export const groupRows = (
  rows: RowWithIssues[],
): Map<string, RowWithIssues[]> => {
  const groups = new Map<string, RowWithIssues[]>();
  for (const row of rows) {
    const category = getPrimaryGroupCategory(row);
    const bucket = groups.get(category);
    if (bucket) bucket.push(row);
    else groups.set(category, [row]);
  }
  return groups;
};

const getPrimaryGroupCategory = (row: RowWithIssues): string => {
  if (
    row.dedupeStatus === 'DUPE_IN_FILE' &&
    row.issues.some((issue) => issue.category === 'DUPE_IN_FILE')
  ) {
    return 'DUPE_IN_FILE';
  }

  return row.issues[0]?.category ?? 'NO_ISSUES';
};

// Group into true duplicate cliques (connected components of `partnerRowNumbers`),
// not forced 2-row pairs: a 3+ row group split into a pair + orphan never
// collapsed to the lone survivor the backend cascade promotes, so the "removed"
// row kept blocking finalize (ENG-4588).
const groupDupeInFileCliques = (rows: RowWithIssues[]): RowWithIssues[][] => {
  const sortedRows = [...rows].sort((a, b) => a.rowNumber - b.rowNumber);
  const byRowNumber = new Map(sortedRows.map((row) => [row.rowNumber, row]));

  // Edges undirected: partner metadata can be one-sided after a reclassify.
  // Partner row numbers absent from this bucket are skipped.
  const adjacency = new Map<RowWithIssues, Set<RowWithIssues>>(
    sortedRows.map((row) => [row, new Set<RowWithIssues>()]),
  );
  for (const row of sortedRows) {
    for (const partnerRowNumber of getDupeInFilePartnerRowNumbers(row)) {
      const partner = byRowNumber.get(partnerRowNumber);
      if (partner === undefined) continue;
      adjacency.get(row)?.add(partner);
      adjacency.get(partner)?.add(row);
    }
  }

  const visited = new Set<RowWithIssues>();
  const groups: RowWithIssues[][] = [];

  for (const row of sortedRows) {
    if (visited.has(row)) continue;
    const component: RowWithIssues[] = [];
    const stack = [row];
    visited.add(row);
    while (stack.length > 0) {
      const node = stack.pop();
      if (node === undefined) continue;
      component.push(node);
      for (const neighbor of adjacency.get(node) ?? []) {
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);
        stack.push(neighbor);
      }
    }
    component.sort((a, b) => a.rowNumber - b.rowNumber);
    groups.push(component);
  }

  // Don't let group order depend on the traversal invariant.
  return groups.sort((a, b) => (a[0]?.rowNumber ?? 0) - (b[0]?.rowNumber ?? 0));
};

const getDupeInFilePartnerRowNumbers = (row: RowWithIssues): number[] => {
  const partnerNumbers = new Set<number>();
  const addPartner = (value: unknown): void => {
    const rowNumber =
      typeof value === 'number'
        ? value
        : typeof value === 'string'
          ? Number(value)
          : NaN;
    if (Number.isInteger(rowNumber)) partnerNumbers.add(rowNumber);
  };
  for (const issue of row.issues) {
    if (issue.category !== 'DUPE_IN_FILE') continue;
    const raw = issue.metadata?.partnerRowNumbers;
    if (Array.isArray(raw)) {
      for (const value of raw) addPartner(value);
    } else {
      // Fall back to the legacy singular `partnerRowNumber` the backend still
      // honors (readPartnerRowNumbers) so older-metadata dupes still group.
      addPartner(issue.metadata?.partnerRowNumber);
    }
  }
  return [...partnerNumbers].sort((a, b) => a - b);
};
